import React, { useEffect, useMemo, useState } from "react";
import {
  Wallet,
  Landmark,
  Receipt,
  TrendingUp,
  CalendarRange,
  Info,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import TaxLiabilityIllustration from "../../components/Admin/payroll/TaxLiabilityIllustration";
import { payrollOpsService } from "../../services/api";
import { formatInr } from "../../components/Admin/payroll/runPayrollUtils";

function money(v) {
  if (v == null || v === "") return "—";
  return formatInr(v);
}

function SummaryCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 text-[hsl(var(--ds-fg))] shadow-[var(--ds-shadow-card)]">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
        {Icon ? <Icon size={14} /> : null}
        {label}
      </div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
      {hint ? (
        <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">{hint}</p>
      ) : null}
    </div>
  );
}

function monthLabel(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return ym || "—";
  const names = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${names[Number(m[2]) - 1]} ${m[1]}`;
}

function buildMonthOptions(financialYear) {
  const m = String(financialYear || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) {
    const now = new Date();
    const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const out = [];
    for (let i = 4; i <= 12; i += 1) out.push(`${y}-${String(i).padStart(2, "0")}`);
    for (let i = 1; i <= 3; i += 1) out.push(`${y + 1}-${String(i).padStart(2, "0")}`);
    return out;
  }
  const start = Number(m[1]);
  const out = [];
  for (let i = 4; i <= 12; i += 1) out.push(`${start}-${String(i).padStart(2, "0")}`);
  for (let i = 1; i <= 3; i += 1) out.push(`${start + 1}-${String(i).padStart(2, "0")}`);
  return out;
}

function componentBucket(type) {
  const t = String(type || "").toLowerCase();
  if (/deduction|tax/.test(t)) return "deduction";
  if (/benefit|employer|reimbursement/.test(t)) return "employer";
  return "earning";
}

/**
 * Employee → Payroll → My Payroll Summary
 * Emoluments, statutory, increments, tax — period selectable.
 * Investment declaration editing stays in Requests → Tax Planning.
 */
export default function EmployeePayrollSummary({ employeeId }) {
  const now = useMemo(() => new Date(), []);
  const [payMonth, setPayMonth] = useState(
    () => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
  );
  const [view, setView] = useState("month"); // month | fy
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showTax, setShowTax] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await payrollOpsService.getMyPayrollSummary({
          payMonth,
          view,
        });
        if (!cancelled) setData(res?.data || null);
      } catch (e) {
        if (!cancelled) {
          setData(null);
          setError(e?.response?.data?.message || e?.message || "Unable to load payroll summary");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [payMonth, view]);

  const fy = data?.financialYear || "";
  const monthOptions = useMemo(
    () => data?.fyMonths || buildMonthOptions(fy),
    [data?.fyMonths, fy]
  );

  const monthTotals = data?.monthTotals || {};
  const ytd = data?.ytdTotals || {};
  const statutory = data?.statutory || {};
  const components = Array.isArray(data?.components) ? data.components : [];
  const increments = Array.isArray(data?.increments) ? data.increments : [];
  const monthlyHistory = Array.isArray(data?.monthlyHistory) ? data.monthlyHistory : [];
  const structure = data?.structure;

  const earnings = components.filter((c) => componentBucket(c.type) === "earning");
  const deductions = components.filter((c) => componentBucket(c.type) === "deduction");
  const employerBits = components.filter((c) => componentBucket(c.type) === "employer");

  const displayGross = view === "fy" ? ytd.grossPay : monthTotals.grossPay;
  const displayNet = view === "fy" ? ytd.netPay : monthTotals.netPay;
  const displayDed = view === "fy" ? ytd.totalDeductions : monthTotals.totalDeductions;
  const displayTds = view === "fy" ? ytd.tds : monthTotals.tds;

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-4 py-3 shadow-[var(--ds-shadow-card)]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-[hsl(var(--ds-fg))]">
              My Payroll Summary
            </h3>
            <p className="mt-1 max-w-3xl text-sm text-[hsl(var(--ds-muted-fg))]">
              Your earnings, statutory deductions, PF/ESI, increments, and tax for the selected
              period. To declare investments or change tax regime, use{" "}
              <strong className="text-[hsl(var(--ds-fg))]">Requests → Tax Planning</strong>.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
              View
              <select
                value={view}
                onChange={(e) => setView(e.target.value)}
                className="mt-1 block rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2.5 py-1.5 text-sm text-[hsl(var(--ds-fg))]"
              >
                <option value="month">Selected month</option>
                <option value="fy">Financial year (YTD)</option>
              </select>
            </label>
            <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
              Month
              <select
                value={payMonth}
                onChange={(e) => setPayMonth(e.target.value)}
                className="mt-1 block min-w-[9rem] rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2.5 py-1.5 text-sm text-[hsl(var(--ds-fg))]"
              >
                {monthOptions.map((ym) => (
                  <option key={ym} value={ym}>
                    {monthLabel(ym)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        {fy ? (
          <p className="mt-2 text-[11px] text-[hsl(var(--ds-muted-fg))]">
            FY {fy}
            {structure?.targetAnnualCtc != null
              ? ` · Structure CTC ${money(structure.targetAnnualCtc)} / year`
              : ""}
            {monthTotals.source === "processed"
              ? " · From processed payroll"
              : monthTotals.source === "structure"
                ? " · From active salary structure (month not processed yet)"
                : ""}
          </p>
        ) : null}
      </div>

      {error ? (
        <div className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-4 py-3 text-sm text-[hsl(var(--ds-fg))]">
          {error}
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          icon={Wallet}
          label={view === "fy" ? "Gross (YTD)" : "Gross pay"}
          value={loading ? "…" : money(displayGross)}
          hint={
            view === "fy"
              ? `${ytd.monthsProcessed || 0} processed month(s)`
              : monthLabel(payMonth)
          }
        />
        <SummaryCard
          icon={Receipt}
          label={view === "fy" ? "Deductions (YTD)" : "Deductions"}
          value={loading ? "…" : money(displayDed)}
          hint={
            displayTds != null ? `incl. TDS ${money(displayTds)}` : "Employee deductions"
          }
        />
        <SummaryCard
          icon={TrendingUp}
          label={view === "fy" ? "Net pay (YTD)" : "Net pay"}
          value={loading ? "…" : money(displayNet)}
          hint={
            monthTotals.paidDays != null && view === "month"
              ? `Paid days ${monthTotals.paidDays}${
                  monthTotals.lopDays ? ` · LOP ${monthTotals.lopDays}` : ""
                }`
              : "Take-home"
          }
        />
        <SummaryCard
          icon={Landmark}
          label="PF employee (YTD)"
          value={loading ? "…" : money(statutory.pfEmployeeYtd)}
          hint={`Employer PF YTD ${money(statutory.pfEmployerYtd)}`}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] xl:col-span-2">
          <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-4 py-3">
            <h4 className="text-sm font-semibold text-[hsl(var(--ds-fg))]">
              Component-wise emoluments
            </h4>
            <span className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
              {view === "fy" ? "YTD amounts" : "Month + YTD"}
            </span>
          </div>
          <div className="overflow-auto">
            <table className="w-full min-w-[520px] text-left text-xs">
              <thead className="bg-[hsl(var(--ds-muted))] text-[11px] uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                <tr>
                  <th className="px-4 py-2 font-semibold">Component</th>
                  <th className="px-4 py-2 font-semibold">Type</th>
                  <th className="px-4 py-2 text-right font-semibold">This month</th>
                  <th className="px-4 py-2 text-right font-semibold">FY YTD</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-[hsl(var(--ds-muted-fg))]">
                      Loading…
                    </td>
                  </tr>
                ) : components.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-[hsl(var(--ds-muted-fg))]">
                      No component amounts yet for this period. After payroll is processed, month
                      and YTD values appear here.
                    </td>
                  </tr>
                ) : (
                  <>
                    {[...earnings, ...employerBits, ...deductions].map((c) => (
                      <tr key={`${c.name}-${c.type}`} className="border-t border-[hsl(var(--ds-border))]">
                        <td className="px-4 py-2 font-medium text-[hsl(var(--ds-fg))]">{c.name}</td>
                        <td className="px-4 py-2 text-[hsl(var(--ds-muted-fg))]">{c.type || "—"}</td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {money(c.monthAmount)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums font-medium">
                          {money(c.ytdAmount)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4">
            <h4 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
              <Landmark size={14} /> Statutory &amp; PF summary (YTD)
            </h4>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-[hsl(var(--ds-muted-fg))]">PF — employee</dt>
                <dd className="tabular-nums font-medium">{money(statutory.pfEmployeeYtd)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-[hsl(var(--ds-muted-fg))]">PF — employer</dt>
                <dd className="tabular-nums font-medium">{money(statutory.pfEmployerYtd)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-[hsl(var(--ds-muted-fg))]">ESI — employee</dt>
                <dd className="tabular-nums font-medium">{money(statutory.esiEmployeeYtd)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-[hsl(var(--ds-muted-fg))]">ESI — employer</dt>
                <dd className="tabular-nums font-medium">{money(statutory.esiEmployerYtd)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-[hsl(var(--ds-muted-fg))]">Professional tax</dt>
                <dd className="tabular-nums font-medium">
                  {money(statutory.professionalTaxYtd)}
                </dd>
              </div>
              <div className="flex justify-between gap-3 border-t border-[hsl(var(--ds-border))] pt-2">
                <dt className="text-[hsl(var(--ds-muted-fg))]">TDS (YTD)</dt>
                <dd className="tabular-nums font-semibold">{money(ytd.tds)}</dd>
              </div>
            </dl>
          </div>

          <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4">
            <h4 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
              <CalendarRange size={14} /> Monthly snapshot
            </h4>
            {monthlyHistory.length === 0 ? (
              <p className="text-xs text-[hsl(var(--ds-muted-fg))]">No processed payroll months in this FY yet.</p>
            ) : (
              <div className="max-h-48 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase text-[hsl(var(--ds-muted-fg))]">
                    <tr>
                      <th className="py-1 text-left">Month</th>
                      <th className="py-1 text-right">Gross</th>
                      <th className="py-1 text-right">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyHistory.map((r) => (
                      <tr key={r.payMonth} className="border-t border-[hsl(var(--ds-border))]">
                        <td className="py-1.5">{monthLabel(r.payMonth)}</td>
                        <td className="py-1.5 text-right tabular-nums">{money(r.grossPay)}</td>
                        <td className="py-1.5 text-right tabular-nums font-medium">
                          {money(r.netPay)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]">
        <div className="border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <h4 className="text-sm font-semibold text-[hsl(var(--ds-fg))]">Increment history</h4>
          <p className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
            Salary revisions recorded for your profile
          </p>
        </div>
        <div className="overflow-auto">
          <table className="w-full min-w-[480px] text-left text-xs">
            <thead className="bg-[hsl(var(--ds-muted))] text-[11px] uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
              <tr>
                <th className="px-4 py-2 font-semibold">Effective</th>
                <th className="px-4 py-2 text-right font-semibold">Previous CTC</th>
                <th className="px-4 py-2 text-right font-semibold">New CTC</th>
                <th className="px-4 py-2 font-semibold">Reason</th>
              </tr>
            </thead>
            <tbody>
              {increments.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-[hsl(var(--ds-muted-fg))]">
                    No salary revisions on record.
                  </td>
                </tr>
              ) : (
                increments.map((r) => (
                  <tr key={r.id} className="border-t border-[hsl(var(--ds-border))]">
                    <td className="px-4 py-2">{r.effectiveDate || "—"}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{money(r.previousCtc)}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">
                      {money(r.newCtc)}
                    </td>
                    <td className="px-4 py-2 text-[hsl(var(--ds-muted-fg))]">{r.reason || r.remarks || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]">
        <button
          type="button"
          onClick={() => setShowTax((v) => !v)}
          className="flex w-full items-center justify-between px-4 py-3 text-left"
        >
          <div>
            <h4 className="text-sm font-semibold text-[hsl(var(--ds-fg))]">Tax liability</h4>
            <p className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
              Same TDS engine as payroll · open full computation with the eye icon
            </p>
          </div>
          {showTax ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        {showTax ? (
          <div className="border-t border-[hsl(var(--ds-border))] p-4">
            {employeeId || data?.employeeId ? (
              <TaxLiabilityIllustration
                employeeId={employeeId || data.employeeId}
                payMonth={payMonth}
              />
            ) : (
              <div className="flex gap-2 rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-3 py-2 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                <Info size={14} className="mt-0.5 shrink-0" />
                Link your login to an employee record to see tax illustration.
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
